import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { DeadLeg, VehicleType } from "../../types/deadLeg";
import { formatUkDate } from "../../utils/dateUtils";

export default function DeadLegDetail() {
  const { id } = useParams();
  const [deadLeg, setDeadLeg] = useState<DeadLeg | null>(null);
  const [vehicleType, setVehicleType] = useState<VehicleType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDeadLegData();
  }, [id]);

  const fetchDeadLegData = async () => {
    try {
      const response = await axiosInstance.get(`/deadlegs/${id}`);
      const deadLegData = response.data;
      setDeadLeg(deadLegData);

      // Fetch vehicle type details
      const vehicleTypeResponse = await axiosInstance.get(`/vehicletypes/${deadLegData.vehicleTypeId}`);
      setVehicleType(vehicleTypeResponse.data);
    } catch (error) {
      setError("Failed to fetch dead leg data");
      console.error("Error fetching dead leg:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return formatUkDate(dateString);
  };

  const formatTime = (timeString: string | null) => {
    return timeString || "N/A";
  };

  const formatPrice = (price: number | string | null) => {
    if (price === null || price === undefined || price === "") return "N/A";
    const num = typeof price === "number" ? price : Number(price);
    return Number.isFinite(num) ? `£${num.toFixed(2)}` : "N/A";
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!deadLeg) return <div>No data found</div>;

  return (
    <div className="space-y-6">
      <PageBreadcrumb pageTitle="Dead Leg Details"  />

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Link
          to={`/dead-legs/edit/${id}`}
          className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
        >
          Edit
        </Link>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        <dl className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">Description</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {deadLeg.description}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">Vehicle Type</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {vehicleType?.description || 'N/A'}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">To Location</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {deadLeg.toLocation}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">To Post Code</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {deadLeg.toPostCode}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">From Date</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {formatDate(deadLeg.fromDate)}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">To Date</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {formatDate(deadLeg.toDate)}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">From Time</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {formatTime(deadLeg.fromTime)}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">To Time</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {formatTime(deadLeg.toTime)}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">Price</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {formatPrice(deadLeg.price)}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">Status</dt>
            <dd className="mt-1">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                deadLeg.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {deadLeg.status ? 'Active' : 'Inactive'}
              </span>
            </dd>
          </div>

          <div className="md:col-span-2">
            <dt className="text-sm font-medium text-gray-500">Remarks</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {deadLeg.remarks || 'N/A'}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
} 